import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Play, Video } from 'lucide-react';
import { isValidVideoUrl } from '@/utils/videoEmbedUtils';

interface Activity {
  id: string;
  title: string;
  description?: string;
  category: string;
  duration: number;
  embed_url?: string;
}

interface ActivityCardProps {
  activity: Activity;
  index: number;
  onWatchVideo: (activity: Activity) => void;
}

export const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  index,
  onWatchVideo,
}) => {
  const hasVideo = activity.embed_url && isValidVideoUrl(activity.embed_url);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-medium">
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="space-y-3">
              <div className="flex-1">
                <h3 className="font-semibold text-foreground mb-1 text-sm sm:text-base">
                  {activity.title}
                </h3>
                {activity.description && (
                  <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                    {activity.description}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <Badge variant="outline" className="text-xs">
                    {activity.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {activity.duration} minutes
                  </span>
                  {hasVideo && (
                    <div className="flex items-center gap-1 text-xs text-primary">
                      <Video className="w-3 h-3" />
                      <span className="hidden sm:inline">Video Available</span>
                      <span className="sm:hidden">Video</span>
                    </div>
                  )}
                </div>
              </div>
              {hasVideo && (
                <div className="flex justify-start">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onWatchVideo(activity)}
                    className="flex items-center gap-2 text-xs sm:text-sm px-3 py-2 w-full sm:w-auto justify-center"
                  >
                    <Play className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>Watch Video</span>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};